import React from "react";
import { evaluate } from "feelin";
import { ResponseValue } from "./types";

/**
 * Hook to evaluate FEEL expressions for visibility and requirement conditions
 *
 * @param responses - Map of question IDs to their response values
 * @param semanticKeyMap - Map of question IDs to their semantic keys
 * @param testMode - Whether test mode is enabled (evaluates predicates when true)
 * @returns Object with evaluatePredicate function
 */
export const useFeelEvaluator = (
  responses: Record<string, ResponseValue>,
  semanticKeyMap: Record<string, string>,
  testMode: boolean
) => {
  const buildContext = React.useCallback(() => {
    const context: Record<string, ResponseValue> = {};

    // Map responses by semanticKey instead of questionId
    Object.entries(semanticKeyMap).forEach(([questionId, semanticKey]) => {
      if (semanticKey && responses[questionId] !== undefined) {
        context[semanticKey] = responses[questionId];
      }
    });

    return context;
  }, [responses, semanticKeyMap]);

  const evaluatePredicate = React.useCallback(
    (predicate: string): boolean => {
      if (!testMode) return true; // In non-test mode, always return true (show/optional)
      if (!predicate) return true;

      try {
        const context = buildContext();
        const result = evaluate(predicate, context);

        // Handle the result - feelin returns { value, warnings }
        if (result && typeof result === "object" && "value" in result) {
          const value = result.value;
          if (typeof value === "boolean") {
            return value;
          }
          // If value is not boolean, try to coerce
          return Boolean(value);
        }

        // Fallback for unexpected result format
        return Boolean(result);
      } catch (error) {
        console.error("FEEL evaluation error:", error, "predicate:", predicate);
        return true; // Default to visible/optional on error
      }
    },
    [testMode, buildContext]
  );

  return { evaluatePredicate };
};
